import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Pagination } from '@/components/ui/pagination';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  AlertCircle,
  ClipboardList,
  Eye,
  Loader2,
  PencilLine,
  Plus,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { dynamicFormsApi } from '@/lib/api/dynamic-forms';
import type { DynamicForm, DynamicFormStatus } from '@/types/dynamic-form';
import { cn } from '@/lib/utils';

function statusBadgeClass(status: DynamicFormStatus) {
  switch (status) {
    case 'published':
      return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400';
    case 'archived':
      return 'border-muted-foreground/25 bg-muted text-muted-foreground';
    default:
      return 'border-amber-500/30 bg-amber-500/10 text-amber-800 dark:text-amber-400';
  }
}

export default function FormsPage() {
  const [forms, setForms] = useState<DynamicForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(12);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | DynamicFormStatus>('all');
  const [deleteTarget, setDeleteTarget] = useState<DynamicForm | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const load = useCallback(
    async (showRefresh = false) => {
      try {
        setError(null);
        if (showRefresh) setRefreshing(true);
        else setLoading(true);

        const res = await dynamicFormsApi.list({
          page,
          limit,
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
          ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
        });

        if (res.statusCode !== 200) {
          throw new Error(res.message || 'Failed to load forms');
        }
        setForms(res.data.forms ?? []);
        setTotal(res.data.pagination?.total ?? 0);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load forms');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [page, limit, debouncedSearch, statusFilter]
  );

  useEffect(() => {
    void load();
  }, [load]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await dynamicFormsApi.remove(deleteTarget._id);
      if (res.statusCode !== 200) {
        throw new Error(res.message || 'Could not delete form');
      }
      toast.success('Form deleted');
      setDeleteTarget(null);
      const lastOnPage = forms.length <= 1 && page > 1;
      if (lastOnPage) {
        setPage((p) => Math.max(1, p - 1));
      } else {
        void load(true);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not delete form');
    } finally {
      setDeleting(false);
    }
  };

  const empty = !loading && forms.length === 0;

  const summaryLine = useMemo(() => {
    if (total === 0) return 'No forms yet';
    const start = (page - 1) * limit + 1;
    const end = Math.min(page * limit, total);
    return `Showing ${start}–${end} of ${total}`;
  }, [total, page, limit]);

  return (
    <div className="mx-auto max-w-7xl space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-muted-foreground">
            <ClipboardList className="h-4 w-4" />
            <span className="text-xs font-medium uppercase tracking-wider">Dynamic forms</span>
          </div>
          <h1 className="text-3xl font-semibold tracking-tight">Forms</h1>
          <p className="max-w-xl text-sm text-muted-foreground">
            Create and manage forms, submissions, and appointment scheduling from one place.
          </p>
        </div>
        <Button className="shrink-0 gap-2 shadow-sm" asChild>
          <Link to="/f/new">
            <Plus className="h-4 w-4" />
            Create form
          </Link>
        </Button>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative flex-1 sm:max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by title or slug…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="h-10 pl-9"
          />
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={statusFilter}
            onValueChange={(v) => {
              setStatusFilter(v as typeof statusFilter);
              setPage(1);
            }}
          >
            <SelectTrigger className="h-10 w-[160px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-10 w-10 shrink-0"
            disabled={refreshing}
            onClick={() => void load(true)}
            aria-label="Refresh list"
          >
            <RefreshCw className={cn('h-4 w-4', refreshing && 'animate-spin')} />
          </Button>
        </div>
      </div>

      <Separator className="opacity-60" />

      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="overflow-hidden border-border/60">
              <CardHeader className="space-y-2">
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-16 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : empty ? (
        <Card className="border-dashed border-2 border-border/80 bg-muted/20">
          <CardContent className="flex flex-col items-center justify-center gap-4 py-16 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <ClipboardList className="h-7 w-7" />
            </div>
            <div className="space-y-1">
              <p className="text-lg font-medium">No forms match your filters</p>
              <p className="text-sm text-muted-foreground">
                Create a form to start collecting responses and bookings.
              </p>
            </div>
            <Button className="gap-2" asChild>
              <Link to="/f/new">
                <Plus className="h-4 w-4" />
                Create form
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">{summaryLine}</p>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {forms.map((form) => (
              <Card
                key={form._id}
                className="group flex flex-col overflow-hidden border-border/70 transition-shadow hover:shadow-md"
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="line-clamp-2 text-lg leading-snug">{form.title}</CardTitle>
                    <Badge
                      variant="outline"
                      className={cn('shrink-0 capitalize', statusBadgeClass(form.status))}
                    >
                      {form.status}
                    </Badge>
                  </div>
                  <CardDescription className="font-mono text-xs">{form.slug}</CardDescription>
                </CardHeader>
                <CardContent className="flex-1 space-y-3 pb-2">
                  {form.description ? (
                    <p className="line-clamp-2 text-sm text-muted-foreground">{form.description}</p>
                  ) : (
                    <p className="text-sm italic text-muted-foreground/70">No description</p>
                  )}
                  <div className="flex flex-wrap gap-4 rounded-lg bg-muted/40 px-3 py-2.5 text-sm">
                    <div>
                      <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                        Submissions
                      </p>
                      <p className="text-lg font-semibold tabular-nums">
                        {form.submissionCount ?? 0}
                      </p>
                    </div>
                    {typeof form.publishedVersion === 'number' && (
                      <div>
                        <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                          Version
                        </p>
                        <p className="text-lg font-semibold tabular-nums">{form.publishedVersion}</p>
                      </div>
                    )}
                  </div>
                  {form.lastSubmissionAt && (
                    <p className="text-xs text-muted-foreground">
                      Last submission{' '}
                      {formatDistanceToNow(new Date(form.lastSubmissionAt), { addSuffix: true })}
                    </p>
                  )}
                </CardContent>
                <CardFooter className="flex flex-col gap-2 border-t border-border/60 bg-muted/20 pt-4 sm:flex-row">
                  <div className="flex flex-1 gap-2">
                    <Button variant="outline" size="sm" className="flex-1 gap-1.5" asChild>
                      <Link to={`/f/${form._id}`}>
                        <Eye className="h-3.5 w-3.5" />
                        View
                      </Link>
                    </Button>
                    <Button size="sm" className="flex-1 gap-1.5 shadow-sm" asChild>
                      <Link to={`/f/${form._id}/edit`}>
                        <PencilLine className="h-3.5 w-3.5" />
                        Edit
                      </Link>
                    </Button>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1.5 border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive"
                    onClick={() => setDeleteTarget(form)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
            className="pt-4"
          />
        </>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && !deleting && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this form?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget ? (
                <>
                  <span className="font-medium text-foreground">{deleteTarget.title}</span> ({deleteTarget.slug})
                  will be removed permanently, including submissions, versions, and appointment data.
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleting}
              onClick={(e) => {
                e.preventDefault();
                void confirmDelete();
              }}
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting…
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
